import React, { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../Sidebar/AdminSidebar';
import AdminNavbar from '../Navbars/AdminNavbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SuperAdminApis } from '../../apis/superAdminApi';
import { Oval } from 'react-loader-spinner';

const formatCurrency = (amount: any) => {
    const num = Number(amount);
    return isNaN(num) ? '₦0.00' : `₦${num.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (date: string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-NG', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const breakdownConfig = [
    { key: '1_3', label: '1-3 days', dataKey: '1_3_days', bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', subText: 'text-yellow-600', activeBg: 'bg-yellow-200', activeBorder: 'border-yellow-400' },
    { key: '4_7', label: '4-7 days', dataKey: '4_7_days', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', subText: 'text-orange-600', activeBg: 'bg-orange-200', activeBorder: 'border-orange-400' },
    { key: '8_14', label: '8-14 days', dataKey: '8_14_days', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', subText: 'text-red-500', activeBg: 'bg-red-200', activeBorder: 'border-red-400' },
    { key: '15_30', label: '15-30 days', dataKey: '15_30_days', bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-700', subText: 'text-red-600', activeBg: 'bg-red-300', activeBorder: 'border-red-500' },
    { key: 'over_30', label: '30+ days', dataKey: 'over_30_days', bg: 'bg-red-200', border: 'border-red-400', text: 'text-red-800', subText: 'text-red-700', activeBg: 'bg-red-400', activeBorder: 'border-red-600' },
];

function PendingFines() {
    const [data, setData] = useState<any>(null);
    const [loader, setLoader] = useState(true);
    const [tableLoader, setTableLoader] = useState(false);

    // Filters
    const [daysRange, setDaysRange] = useState('');
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('latest');

    const fetchStats = useCallback(async (page = 1, showFullLoader = false) => {
        if (showFullLoader) setLoader(true);
        else setTableLoader(true);

        try {
            const response: any = await SuperAdminApis.getPendingFinesStats({
                days_range: daysRange,
                search,
                sort,
                page: String(page),
                per_page: '20',
            });
            if (response?.data?.status) {
                setData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching pending fines:', error);
            toast.error('Failed to load pending fines data');
        } finally {
            setLoader(false);
            setTableLoader(false);
        }
    }, [daysRange, search, sort]);

    useEffect(() => {
        fetchStats(1, true);
    }, []);

    // When breakdown filter or sort changes, refetch from page 1
    useEffect(() => {
        fetchStats(1);
    }, [daysRange, sort]);

    const handleSearch = () => {
        fetchStats(1);
    };

    const handleBreakdownClick = (rangeKey: string) => {
        // Toggle: click same filter again to clear it
        if (daysRange === rangeKey) {
            setDaysRange('');
        } else {
            setDaysRange(rangeKey);
        }
    };

    const handleClearFilters = () => {
        setDaysRange('');
        setSearch('');
        setSort('latest');
        setTimeout(() => fetchStats(1), 0);
    };

    const paginator = (page: number) => {
        fetchStats(page);
    };

    return (
        <>
            <AdminSidebar title="Pending Fines" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="Pending Fines" />
                <div className="lg:px-10 pb-6 px-6 bg-[#FAFAFA] min-h-screen">

                    {loader ? (
                        <div className="flex justify-center py-20">
                            <Oval visible={true} height="50" width="50" color="#E04343" secondaryColor="#FCBF94" ariaLabel="oval-loading" />
                        </div>
                    ) : data ? (
                        <>
                            {/* Settings Banner */}
                            <div className={`rounded-[10px] p-4 mb-5 border ${data.settings?.is_enabled ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                <div className="flex justify-between items-center">
                                    <div>
                                        <p className={`text-[14px] font-[600] ${data.settings?.is_enabled ? 'text-green-800' : 'text-red-800'}`}>
                                            {data.settings?.is_enabled ? '✅ Fine System Active' : '❌ Fine System Disabled'}
                                        </p>
                                        <p className="text-[12px] text-[#808080] mt-1">
                                            Rate: {formatCurrency(data.settings?.base_fine_rate)}/day | Grace Period: {data.settings?.grace_period_hours}h
                                        </p>
                                    </div>
                                    <button onClick={() => fetchStats(1, true)} className="bg-white border border-gray-300 text-[12px] px-3 py-1.5 rounded-md hover:bg-gray-50">
                                        Refresh
                                    </button>
                                </div>
                            </div>

                            {/* Overview Stats */}
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                                <div className="bg-white rounded-[10px] p-3 text-center shadow-sm border">
                                    <p className="text-[22px] font-[700] text-[#333333]">{data.overview?.total_ofd_shipments}</p>
                                    <p className="text-[11px] text-[#808080]">Total in OFD</p>
                                </div>
                                <div className="bg-blue-50 rounded-[10px] p-3 text-center shadow-sm border border-blue-200">
                                    <p className="text-[22px] font-[700] text-blue-700">{data.overview?.within_grace_period}</p>
                                    <p className="text-[11px] text-blue-600">Within Grace</p>
                                </div>
                                <div className="bg-red-50 rounded-[10px] p-3 text-center shadow-sm border border-red-200">
                                    <p className="text-[22px] font-[700] text-red-700">{data.overview?.shipments_with_fines}</p>
                                    <p className="text-[11px] text-red-600">With Fines</p>
                                </div>
                                <div className="bg-orange-50 rounded-[10px] p-3 text-center shadow-sm border border-orange-200">
                                    <p className="text-[22px] font-[700] text-orange-700">{formatCurrency(data.overview?.total_pending_amount)}</p>
                                    <p className="text-[11px] text-orange-600">Total Pending Fines</p>
                                </div>
                                <div className="bg-purple-50 rounded-[10px] p-3 text-center shadow-sm border border-purple-200">
                                    <p className="text-[22px] font-[700] text-purple-700">{data.overview?.avg_fine_days}</p>
                                    <p className="text-[11px] text-purple-600">Avg Days Late</p>
                                </div>
                                <div className="bg-gray-50 rounded-[10px] p-3 text-center shadow-sm border">
                                    <p className="text-[22px] font-[700] text-[#333333]">{data.overview?.max_fine_days}</p>
                                    <p className="text-[11px] text-[#808080]">Max Days Late</p>
                                </div>
                            </div>

                            {/* Fine Breakdown — Clickable Filters */}
                            {data.breakdown && (
                                <div className="bg-white rounded-[10px] p-4 shadow-sm border mb-5">
                                    <div className="flex justify-between items-center mb-3">
                                        <h3 className="text-[14px] font-[600] text-[#333333]">Fine Breakdown by Late Days</h3>
                                        {daysRange && (
                                            <button onClick={() => setDaysRange('')} className="text-[11px] text-[#E04343] hover:underline font-[500]">
                                                Clear Filter ✕
                                            </button>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                        {breakdownConfig.map((item) => {
                                            const isActive = daysRange === item.key;
                                            return (
                                                <div
                                                    key={item.key}
                                                    onClick={() => handleBreakdownClick(item.key)}
                                                    className={`rounded-lg p-3 text-center border cursor-pointer transition-all duration-200 hover:shadow-md ${isActive ? `${item.activeBg} ${item.activeBorder} ring-2 ring-offset-1 ring-red-300` : `${item.bg} ${item.border} hover:scale-[1.02]`}`}
                                                >
                                                    <p className={`text-[18px] font-[700] ${item.text}`}>
                                                        {data.breakdown[item.dataKey]}
                                                    </p>
                                                    <p className={`text-[11px] ${item.subText}`}>{item.label}</p>
                                                    {isActive && <p className="text-[9px] mt-1 font-[600] text-red-700">FILTERED</p>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Search + Sort Bar */}
                            <div className="bg-white rounded-[10px] p-4 shadow-sm border mb-5">
                                <div className="flex flex-wrap gap-3 items-end">
                                    <div className="flex-1 min-w-[200px]">
                                        <label className="block text-[12px] text-[#808080] mb-1">Search</label>
                                        <input
                                            type="text"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                            placeholder="Shipment #, fleet name, city, state..."
                                            className="border border-gray-300 text-sm rounded-md block w-full p-2 text-gray-600"
                                        />
                                    </div>
                                    <div className="min-w-[150px]">
                                        <label className="block text-[12px] text-[#808080] mb-1">Sort By</label>
                                        <select value={sort} onChange={(e) => setSort(e.target.value)} className="border border-gray-300 text-sm rounded-md block w-full p-2 text-gray-600">
                                            <option value="latest">Most Recent OFD</option>
                                            <option value="oldest">Longest in OFD</option>
                                            <option value="highest_fine">Highest Fine</option>
                                        </select>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={handleSearch} className="bg-[#E04343] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-600">Search</button>
                                        <button onClick={handleClearFilters} className="bg-gray-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-600">Clear</button>
                                    </div>
                                </div>
                                {daysRange && (
                                    <div className="mt-2">
                                        <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 text-[11px] font-[600] px-2 py-1 rounded-full">
                                            Showing: {breakdownConfig.find(b => b.key === daysRange)?.label || daysRange}
                                            <button onClick={() => setDaysRange('')} className="ml-1 hover:text-red-900">✕</button>
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Shipments Table (Paginated) */}
                            <div className="bg-white rounded-[10px] shadow-sm border mb-5">
                                <div className="p-4 border-b flex justify-between items-center">
                                    <h3 className="text-[14px] font-[600] text-[#333333]">
                                        Shipments with Pending Fines
                                        {data.shipments?.total > 0 && (
                                            <span className="text-[11px] text-[#808080] font-[400] ml-2">
                                                ({data.shipments.from}–{data.shipments.to} of {data.shipments.total})
                                            </span>
                                        )}
                                    </h3>
                                    {tableLoader && <Oval visible height="20" width="20" color="#E04343" secondaryColor="#FCBF94" ariaLabel="loading" />}
                                </div>

                                {/* Desktop Table */}
                                <div className="hidden lg:block overflow-x-auto">
                                    <table className="w-full text-sm text-left text-gray-500">
                                        <thead className="text-[11px] text-[#030229] bg-[#FAFAFA]">
                                            <tr>
                                                <th className="px-3 py-2">S/N</th>
                                                <th className="px-3 py-2">Shipment #</th>
                                                <th className="px-3 py-2">OFD Since</th>
                                                <th className="px-3 py-2">Days Late</th>
                                                <th className="px-3 py-2">Fine Amount</th>
                                                <th className="px-3 py-2">Rider</th>
                                                <th className="px-3 py-2">Fleet</th>
                                                <th className="px-3 py-2">Manifest</th>
                                                <th className="px-3 py-2">Receiver</th>
                                                <th className="px-3 py-2">Destination</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.shipments?.data?.length > 0 ? (
                                                data.shipments.data.map((s: any, i: number) => (
                                                    <tr key={i} className="bg-white border-b hover:bg-gray-50">
                                                        <td className="px-3 py-2 text-[12px]">{((data.shipments.current_page - 1) * 20) + i + 1}</td>
                                                        <td className="px-3 py-2 text-[12px] font-[600] text-[#333333]">{s.ShipmentNumber}</td>
                                                        <td className="px-3 py-2 text-[11px] text-[#808080]">{formatDate(s.ofd_created_at)}</td>
                                                        <td className="px-3 py-2">
                                                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-[600] ${s.pending_fine_days > 14 ? 'bg-red-100 text-red-700' : s.pending_fine_days > 7 ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                                {s.pending_fine_days} days
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2 text-[12px] font-[600] text-red-600">{formatCurrency(s.pending_fine_amount)}</td>
                                                        <td className="px-3 py-2">
                                                            {s.rider_first_name ? (
                                                                <div>
                                                                    <p className="text-[11px] font-[500] text-[#333333]">{s.rider_first_name} {s.rider_last_name || ''}</p>
                                                                    <p className="text-[9px] text-[#808080]">{s.rider_email}</p>
                                                                </div>
                                                            ) : (
                                                                <span className="text-gray-300">—</span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2 text-[11px]">{s.fleet_name || '—'}</td>
                                                        <td className="px-3 py-2 text-[11px]">{s.run_manifest_name || '—'}</td>
                                                        <td className="px-3 py-2 text-[11px]">{s.ReceiverName || '—'}</td>
                                                        <td className="px-3 py-2 text-[11px]">{s.ReceiverAddCity || ''}{s.ReceiverAddState ? `, ${s.ReceiverAddState}` : ''}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={10} className="text-center py-10 text-gray-400">
                                                        {daysRange ? 'No shipments in this range' : 'No shipments with pending fines'}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Cards */}
                                <div className="lg:hidden p-3 space-y-2">
                                    {data.shipments?.data?.length > 0 ? (
                                        data.shipments.data.map((s: any, i: number) => (
                                            <div key={i} className="bg-gray-50 rounded-lg p-3 border">
                                                <div className="flex justify-between items-start mb-2">
                                                    <p className="text-[12px] font-[600] text-[#333333]">{s.ShipmentNumber}</p>
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-[600] ${s.pending_fine_days > 14 ? 'bg-red-100 text-red-700' : s.pending_fine_days > 7 ? 'bg-orange-100 text-orange-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                                        {s.pending_fine_days} days
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-1 text-[11px]">
                                                    <div><span className="text-[#808080]">Fine:</span> <span className="font-[600] text-red-600">{formatCurrency(s.pending_fine_amount)}</span></div>
                                                    <div><span className="text-[#808080]">Rider:</span> <span className="font-[500]">{s.rider_first_name || '—'} {s.rider_last_name || ''}</span></div>
                                                    <div><span className="text-[#808080]">Fleet:</span> {s.fleet_name || '—'}</div>
                                                    <div><span className="text-[#808080]">Receiver:</span> {s.ReceiverName || '—'}</div>
                                                    <div><span className="text-[#808080]">Dest:</span> {s.ReceiverAddCity || ''}{s.ReceiverAddState ? `, ${s.ReceiverAddState}` : ''}</div>
                                                    <div className="col-span-2"><span className="text-[#808080]">OFD Since:</span> {formatDate(s.ofd_created_at)}</div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-8 text-gray-400">
                                            {daysRange ? 'No shipments in this range' : 'No shipments with pending fines'}
                                        </div>
                                    )}
                                </div>

                                {/* Pagination */}
                                {data.shipments?.last_page > 1 && (
                                    <div className="p-4 flex justify-end border-t">
                                        {data.shipments.links?.map((link: any, index: number) => (
                                            <button
                                                key={index}
                                                onClick={() => {
                                                    if (link.url) {
                                                        const page = link.label === 'Next &raquo;'
                                                            ? data.shipments.current_page + 1
                                                            : link.label === '&laquo; Previous'
                                                                ? data.shipments.current_page - 1
                                                                : parseInt(link.label);
                                                        paginator(page);
                                                    }
                                                }}
                                                disabled={!link.url || link.active}
                                                className={'mx-1 py-1 px-2 rounded text-sm ' + (link.active ? 'bg-[#E04343] text-white' : link.url ? 'bg-gray-200 text-black hover:bg-gray-300' : 'bg-gray-100 text-gray-400')}
                                            >
                                                {link.label === '&laquo; Previous' ? '← Prev' : link.label === 'Next &raquo;' ? 'Next →' : link.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Recent Fine Deductions */}
                            {data.recent_deductions?.length > 0 && (
                                <div className="bg-white rounded-[10px] shadow-sm border">
                                    <div className="p-4 border-b">
                                        <h3 className="text-[14px] font-[600] text-[#333333]">Recent Fine Deductions (at POD)</h3>
                                    </div>

                                    <div className="hidden lg:block overflow-x-auto">
                                        <table className="w-full text-sm text-left text-gray-500">
                                            <thead className="text-[11px] text-[#030229] bg-[#FAFAFA]">
                                                <tr>
                                                    <th className="px-3 py-2">Shipment #</th>
                                                    <th className="px-3 py-2">Gross Rate</th>
                                                    <th className="px-3 py-2">Fine</th>
                                                    <th className="px-3 py-2">Facility Fine</th>
                                                    <th className="px-3 py-2">Days Late</th>
                                                    <th className="px-3 py-2">Net Earnings</th>
                                                    <th className="px-3 py-2">POD Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.recent_deductions.map((d: any, i: number) => (
                                                    <tr key={i} className="bg-white border-b hover:bg-gray-50">
                                                        <td className="px-3 py-2 text-[12px] font-[600] text-[#333333]">{d.shipment_number}</td>
                                                        <td className="px-3 py-2 text-[12px]">{formatCurrency(d.gross_amount)}</td>
                                                        <td className="px-3 py-2 text-[12px] font-[600] text-red-600">-{formatCurrency(d.fine_amount)}</td>
                                                        <td className="px-3 py-2 text-[12px] font-[600] text-purple-600">
                                                            {Number(d.facility_fine_amount || 0) > 0 ? `-${formatCurrency(d.facility_fine_amount)}` : <span className="text-gray-300">—</span>}
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <span className="px-2 py-0.5 rounded-full text-[10px] font-[600] bg-red-100 text-red-700">
                                                                {d.fine_days} days
                                                            </span>
                                                        </td>
                                                        <td className="px-3 py-2 text-[12px] font-[600] text-green-600">{formatCurrency(d.amount)}</td>
                                                        <td className="px-3 py-2 text-[11px] text-[#808080]">{formatDate(d.created_at)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="lg:hidden p-3 space-y-2">
                                        {data.recent_deductions.map((d: any, i: number) => (
                                            <div key={i} className="bg-gray-50 rounded-lg p-3 border">
                                                <div className="flex justify-between items-start mb-1">
                                                    <p className="text-[12px] font-[600] text-[#333333]">{d.shipment_number}</p>
                                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-[600] bg-red-100 text-red-700">{d.fine_days} days</span>
                                                </div>
                                                <div className="grid grid-cols-3 gap-1 text-[11px]">
                                                    <div><span className="text-[#808080]">Gross:</span> {formatCurrency(d.gross_amount)}</div>
                                                    <div><span className="text-[#808080]">Fine:</span> <span className="text-red-600">-{formatCurrency(d.fine_amount)}</span></div>
                                                    <div><span className="text-[#808080]">Net:</span> <span className="text-green-600">{formatCurrency(d.amount)}</span></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* ═══════════════════════════════════════════ */}
                            {/* FACILITY OVERSTAY FINES SECTION            */}
                            {/* ═══════════════════════════════════════════ */}
                            {data.facility_overview && (
                                <>
                                    <div className="mt-8 mb-5">
                                        <h2 className="text-[16px] font-[700] text-[#333333] flex items-center gap-2">
                                            🏢 Facility Overstay Fines
                                        </h2>
                                        <p className="text-[12px] text-[#808080]">Shipments stuck at facility beyond the {data.settings?.facility_grace_period_hours || 72}h grace period</p>
                                    </div>

                                    {/* Facility Settings Banner */}
                                    <div className={`rounded-[10px] p-4 mb-5 border ${data.settings?.facility_fine_enabled ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'}`}>
                                        <p className={`text-[14px] font-[600] ${data.settings?.facility_fine_enabled ? 'text-purple-800' : 'text-gray-600'}`}>
                                            {data.settings?.facility_fine_enabled ? '✅ Facility Fine Active' : '❌ Facility Fine Disabled'}
                                        </p>
                                        <p className="text-[12px] text-[#808080] mt-1">
                                            Rate: {formatCurrency(data.settings?.facility_base_fine_rate)}/day | Grace Period: {data.settings?.facility_grace_period_hours}h
                                        </p>
                                    </div>

                                    {/* Facility Overview Stats */}
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
                                        <div className="bg-white rounded-[10px] p-3 text-center shadow-sm border">
                                            <p className="text-[22px] font-[700] text-[#333333]">{data.facility_overview?.total_facility_shipments}</p>
                                            <p className="text-[11px] text-[#808080]">Total at Facility</p>
                                        </div>
                                        <div className="bg-purple-50 rounded-[10px] p-3 text-center shadow-sm border border-purple-200">
                                            <p className="text-[22px] font-[700] text-purple-700">{data.facility_overview?.within_grace_period}</p>
                                            <p className="text-[11px] text-purple-600">Within Grace</p>
                                        </div>
                                        <div className="bg-red-50 rounded-[10px] p-3 text-center shadow-sm border border-red-200">
                                            <p className="text-[22px] font-[700] text-red-700">{data.facility_overview?.shipments_with_fines}</p>
                                            <p className="text-[11px] text-red-600">With Fines</p>
                                        </div>
                                        <div className="bg-orange-50 rounded-[10px] p-3 text-center shadow-sm border border-orange-200">
                                            <p className="text-[22px] font-[700] text-orange-700">{formatCurrency(data.facility_overview?.total_pending_amount)}</p>
                                            <p className="text-[11px] text-orange-600">Total Pending Fines</p>
                                        </div>
                                        <div className="bg-purple-50 rounded-[10px] p-3 text-center shadow-sm border border-purple-200">
                                            <p className="text-[22px] font-[700] text-purple-700">{data.facility_overview?.avg_fine_days}</p>
                                            <p className="text-[11px] text-purple-600">Avg Days Late</p>
                                        </div>
                                        <div className="bg-gray-50 rounded-[10px] p-3 text-center shadow-sm border">
                                            <p className="text-[22px] font-[700] text-[#333333]">{data.facility_overview?.max_fine_days}</p>
                                            <p className="text-[11px] text-[#808080]">Max Days Late</p>
                                        </div>
                                    </div>

                                    {/* Facility Breakdown */}
                                    {data.facility_breakdown && (
                                        <div className="bg-white rounded-[10px] p-4 shadow-sm border mb-5">
                                            <h3 className="text-[14px] font-[600] text-[#333333] mb-3">Facility Fine Breakdown by Days</h3>
                                            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                                {breakdownConfig.map((item) => (
                                                    <div key={`fac-${item.key}`} className={`rounded-lg p-3 text-center border ${item.bg} ${item.border}`}>
                                                        <p className={`text-[18px] font-[700] ${item.text}`}>{data.facility_breakdown[item.dataKey]}</p>
                                                        <p className={`text-[11px] ${item.subText}`}>{item.label}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        <div className="bg-white rounded-[10px] p-8 text-center text-gray-400">No data available</div>
                    )}
                </div>
            </div>

            <ToastContainer position="top-right" autoClose={2000} hideProgressBar={true} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
        </>
    );
}

export default PendingFines;